import { Injectable, computed, effect, inject } from '@angular/core';

import { CAPABILITIES, type Capability } from '../model/capabilities';
import { ActivePartnerContext } from '../session/active-partner-context';
import { SessionService } from '../session/session.service';
import { SUPER_ADMIN_ROLE, type PartnerSummary } from '../session/session.types';
import { CAPABILITY_SOURCE } from './capability-source';

/**
 * The one partner area the user staffs, or `null` when the choice is ambiguous
 * — they staff none, or they staff several and nobody has picked.
 *
 * "Sole" is counted over the best tier available, not over the whole list
 * (HAL-143). Suspended seats are carried in the token now, so counting them
 * alongside working ones would stop auto-selecting for anyone who picks up a
 * paused seat anywhere — while filtering them out outright would leave a
 * suspended-only member with no active partner at all, and the membership gate
 * needs one to ask about before it can say the seat was paused.
 *
 * Pulled out of the constructor effect so that effect and
 * {@link CapabilitiesService.resolveActivePartnerId} cannot answer differently.
 */
function soleUsablePartner(access: readonly PartnerSummary[]): string | null {
  const usable = access.filter((partner) => !partner.suspended);
  const candidates = usable.length ? usable : access;
  return candidates.length === 1 ? candidates[0].partnerId : null;
}

/**
 * The frontend gating primitive — the **fine altitude** of the two-altitudes
 * model. Resolves the current user's effective capabilities for the active
 * partner area and exposes `has` / `hasAll` / `hasAny` for guards, the
 * `*hasCapability` directive, and templates.
 *
 * The active partner lives in {@link ActivePartnerContext} (the tiny seam the
 * `authInterceptor` reads for the `creatorSub` header); this service is its
 * single **writer**. It auto-selects the sole partner when the user staffs
 * exactly one, and otherwise waits for an app to set one (partner switcher /
 * route — fp1-05). Caps themselves come from the swappable
 * {@link CAPABILITY_SOURCE}, which today reads the `roles_token`.
 *
 * **UI gating only** — the backend `CapabilitiesGuard` is the real enforcement,
 * and a super admin is treated as full access to mirror the server-side bypass.
 */
@Injectable({ providedIn: 'root' })
export class CapabilitiesService {
  private readonly session = inject(SessionService);
  private readonly activePartner = inject(ActivePartnerContext);
  private readonly source = inject(CAPABILITY_SOURCE);

  /** The partner area capabilities resolve against; `null` until one is chosen. */
  readonly activePartnerId = this.activePartner.activePartnerId;

  /**
   * The active user's effective capabilities for {@link activePartnerId}. A
   * super admin gets the full catalogue (UI parity with the backend bypass);
   * with no active partner the set is empty.
   */
  readonly capabilities = computed<readonly Capability[]>(() => {
    if (this.session.role() === SUPER_ADMIN_ROLE) {
      return CAPABILITIES;
    }
    const id = this.activePartnerId();
    return id ? this.source.resolve(id) : [];
  });

  constructor() {
    // Auto-select the sole partner the user staffs, for everything that reads
    // `activePartnerId()` without going through resolveActivePartnerId() — the
    // switcher, the membership service, every feature's data layer. With zero or
    // several the choice is ambiguous, so leave it null for an app to set
    // explicitly. Only writes null → id, so it settles after one pass.
    effect(() => {
      const sole = soleUsablePartner(this.session.partnerAccess());
      if (this.activePartnerId() === null && sole !== null) {
        this.activePartner.setActivePartnerId(sole);
      }
    });
  }

  /** Point capability resolution at a partner area (or clear it with `null`). */
  setActivePartnerId(id: string | null): void {
    this.activePartner.setActivePartnerId(id);
  }

  /**
   * The active partner area, resolving it **now** when the choice is
   * unambiguous rather than waiting for the constructor effect to settle.
   *
   * That effect reaches the same answer, but an `effect` runs on the next flush
   * and route guards run before one — so on a cold load a single-partner user's
   * `activePartnerId()` is still `null` when a guard asks, and a guard that
   * read that as "staffs no partner" silently discarded the navigation
   * (HAL-149). Everything needed to answer is already decoded from the
   * `roles_token`, so there is nothing to wait for; this just reads it. The
   * result is written back, so the choice persists and the `creatorSub` header
   * is right for the first request that follows.
   *
   * `null` means genuinely ambiguous — no partner areas at all, or several with
   * none picked — and it is the caller's job to decide what that deserves.
   */
  resolveActivePartnerId(): string | null {
    const current = this.activePartnerId();
    if (current !== null) {
      return current;
    }
    const sole = soleUsablePartner(this.session.partnerAccess());
    if (sole !== null) {
      this.activePartner.setActivePartnerId(sole);
    }
    return sole;
  }

  /**
   * Whether the user may take `partnerId` as their acting context — a member of
   * that area, or a super admin (empty `partnerAccess` by design, acting on
   * every area, mirroring the server-side bypass).
   *
   * Membership, not capability. It answers "is this partner id one of theirs?",
   * which is what a `partner/:id` URL has to be checked against before its id is
   * committed to the context (HAL-150). What they may *do* once there is still
   * {@link has} / {@link hasAll}.
   */
  canActOnPartner(partnerId: string): boolean {
    if (this.session.role() === SUPER_ADMIN_ROLE) {
      return true;
    }
    return this.session.partnerAccess().some((partner) => partner.partnerId === partnerId);
  }

  /** True when the active user holds `cap` in the active partner area. */
  has(cap: Capability): boolean {
    return this.capabilities().includes(cap);
  }

  /** True when the active user holds every one of `caps`. */
  hasAll(...caps: Capability[]): boolean {
    return caps.every((cap) => this.has(cap));
  }

  /** True when the active user holds at least one of `caps`. */
  hasAny(...caps: Capability[]): boolean {
    return caps.some((cap) => this.has(cap));
  }

  /** Re-read the session token (and any endpoint-backed source) to re-resolve caps. */
  refresh(): void {
    this.session.refresh();
    this.source.refresh?.();
  }
}
